import React from 'react';
import moment from 'moment';
import classNames from 'classnames';
import _ from 'lodash';
import {connect} from 'react-redux';
import {Field, reduxForm, submit} from 'redux-form';

import {MoneyField, TextField, SearchField, DateField} from 'components/Fields';
import Label from 'components/Label';
import FormField from 'components/FormField';
import ErrorBlock from 'components/ErrorBlock';
import Button from 'components/Button';

import {required, validDate} from 'utils/validators';
import {formatDateForApi, getCurrentMonthFirstDay} from 'utils/datetime';

import {
  MODALS,
  FETCH_SCHEDULE_DELAY,
  addRefund,
  fetchCanCreateFinanceItemForPastMonths
} from 'ducks/tenancy';

import {delayedAction, composeActions} from 'ducks/common';
import {selectModal} from 'ducks/modals';

import './styles.css';

const formSubmit = (values, dispatch, props) => {
  const data = {
    ...values,
    paidTo: _.get(values, 'paidTo.id'),
    tenancyId: props.tenancyId,
    creditNote: props.creditNoteId,
    datePaid: formatDateForApi(values.datePaid)
  };

  const extraActions = props.getExtraActions();
  const delayedExtraActions = props.delayExtraActions({
    tenancyId: props.tenancyId,
    scheduleType: props.scheduleType
  });

  if (!_.isEmpty(delayedExtraActions)) {
    const delayedFetchSchedule = () =>
      delayedAction({
        delay: FETCH_SCHEDULE_DELAY,
        action: () => composeActions(delayedExtraActions)
      });
    extraActions.push(delayedFetchSchedule);
  }

  dispatch(addRefund(data, extraActions));
};

class ReceiveCashRefundForm extends React.Component<any> {
  componentDidMount() {
    const {locationRef} = this.props;

    const fetchCanCreateFinanceItemForPastMonthsExtraAction = this.props.storeDataExtraActions(
      data => ({
        canCreatePaymentForPastMonths: _.get(
          data,
          'canCreateFinanceItemsForPastMonths'
        )
      })
    );

    this.props.fetchCanCreateFinanceItemForPastMonths(
      {locationRef: locationRef},
      fetchCanCreateFinanceItemForPastMonthsExtraAction
    );
  }

  render() {
    const {
      className,
      handleSubmit,
      submitting,
      dispatch,
      error,
      xeroAccounts,
      canCreatePaymentForPastMonths
    } = this.props;

    const triggerSubmit = () => {
      dispatch(submit('receiveCashRefund'));
    };
    const xeroAccountOptions = xeroAccounts.map(acc => ({
      value: `${acc.id} - ${acc.label}`,
      label: acc.label,
      id: acc.id
    }));

    let datepickerProps: any = undefined;
    const currentMonthFirstDay: any = getCurrentMonthFirstDay();

    if (!canCreatePaymentForPastMonths) {
      datepickerProps = {
        disabledDays: {before: new Date(currentMonthFirstDay)}
      };
    }

    const DatePickerWithPermissionsOverlay = ({
      children,
      onBlur,
      onFocus,
      tabIndex,
      month // month here is the first day of the current selected month
    }: any) => {
      const shouldShowWarning =
        !canCreatePaymentForPastMonths && moment(month) < currentMonthFirstDay;

      return (
        <div
          className="DayPickerInput-OverlayWrapper"
          onBlur={onBlur}
          onFocus={onFocus}
          tabIndex={tabIndex}>
          <div className="DayPickerInput-Overlay">
            {children}
            {shouldShowWarning && (
              <div className="colab-centered colab-bold ">
                You do not have the permission!
              </div>
            )}
          </div>
        </div>
      );
    };

    return (
      <div className="formContainer">
        {error && <ErrorBlock>{error}</ErrorBlock>}
        <form
          onSubmit={handleSubmit}
          className={classNames(className, 'ReceiveCashRefundForm')}>
          <div className="fields">
            <FormField>
              <Label>Amount paid</Label>
              <Field
                name="amount"
                validate={[required]}
                component={MoneyField}
              />
            </FormField>
            <FormField>
              <Label>Date paid</Label>
              <Field
                name="datePaid"
                component={DateField}
                validate={[validDate]}
                dayPickerProps={datepickerProps}
                overlayComponent={DatePickerWithPermissionsOverlay}
              />
            </FormField>
            <FormField>
              <Label>Paid to</Label>
              <Field
                name="paidTo"
                validate={[required]}
                component={SearchField}
                {...{options: xeroAccountOptions}}
              />
            </FormField>
            <FormField>
              <Label>Reference</Label>
              <Field
                placeholder="Reference"
                name="reference"
                validate={[required]}
                component={TextField}
              />
            </FormField>
          </div>
          <Button
            onClick={triggerSubmit}
            blue={!submitting}
            isLoading={submitting}
            disabled={submitting}>
            Add Refund
          </Button>
        </form>
      </div>
    );
  }
}

const mapStateToProps = state => {
  const modal = selectModal(state, MODALS.CREDIT_NOTE_DETAIL);
  const {data: {xeroAccounts, canCreatePaymentForPastMonths}} = modal;

  return {
    xeroAccounts,
    canCreatePaymentForPastMonths
  };
};

const mapDispatchToProps = {
  fetchCanCreateFinanceItemForPastMonths
};

export default connect(mapStateToProps, mapDispatchToProps)(
  reduxForm({
    form: 'receiveCashRefund',
    onSubmit: formSubmit
  })(ReceiveCashRefundForm)
);
